import { ServiceRecoveryTargets } from './ServiceRecoveryTargets'
import { useEffect, useState, useMemo, useRef, type ReactNode } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Database, Info, Layers, List, Radar, Search, Share2 } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '@/components/ui/form'
import { classificationsApi } from '@/features/classifications/api'
import { locationsApi } from '@/features/locations/api'
import { objectsApi } from '../api'
import { formatSecondsToDHM } from '../utils/formatTime'
import { IconPicker } from './IconPicker'
import type { ObjectFormData } from '../schema'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface ObjectDetailsTabProps {
    form: UseFormReturn<ObjectFormData>
    isEditing: boolean
    isService?: boolean
}

/** Small info icon + tooltip, matching the Premium Analytics hint. */
function InfoHint({ text }: { text: string }) {
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="ml-1 inline-flex cursor-help text-muted-foreground">
                        <Info className="h-3.5 w-3.5" />
                    </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}

/**
 * A labelled group of related fields. The heading is deliberately quiet — small,
 * uppercase, muted, with a hairline rule running to the edge — so it organises the
 * form without competing with the dialog title. Both colours are theme tokens
 * (`--muted-foreground`, `--border`), which every one of the four themes defines,
 * so it reads correctly in light, dark and both enhanced-contrast variants.
 */
function FormSection({
    title,
    children,
    className,
}: {
    title: string
    children: ReactNode
    className?: string
}) {
    return (
        <section className={className}>
            <div className="flex items-center gap-3 pb-4">
                <h3 className="shrink-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {title}
                </h3>
                <span aria-hidden="true" className="h-px flex-1 bg-border" />
            </div>
            {children}
        </section>
    )
}

/**
 * Marks a field as required. A bare red asterisk reads as an error and says
 * nothing to a screen reader; a muted tag states the rule plainly instead.
 */
function RequiredTag() {
    const { t } = useTranslation()
    return (
        <span className="ml-2 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('common.required', 'Required')}
        </span>
    )
}

/**
 * Shared shell for the tick-box options so they read as one considered set rather
 * than as loose ticks floating between unrelated inputs.
 */
const OPTION_ROW =
    'flex flex-row items-center gap-3 space-y-0 rounded-md border border-border bg-muted/30 px-3 py-2.5 transition-colors hover:bg-muted/50'

/** Shared label treatment inside an option row. */
const OPTION_LABEL = 'flex min-w-0 cursor-pointer items-center gap-2'

export function ObjectDetailsTab({ form, isEditing, isService = false }: ObjectDetailsTabProps) {
    const { t } = useTranslation()
    const [objectIDError, setObjectIDError] = useState<string | null>(null)
    const [isCheckingObjectID, setIsCheckingObjectID] = useState(false)
    const [technologySearch, setTechnologySearch] = useState('')
    const [teamSearch, setTeamSearch] = useState('')
    const [backupSolutionSearch, setBackupSolutionSearch] = useState('')
    const [datastoreConfirmDialog, setDatastoreConfirmDialog] = useState<{
        open: boolean
        type: 'set' | 'clear' | null
        pendingTechnology: number | null
    }>({
        open: false,
        type: null,
        pendingTechnology: null,
    })
    const objectID = form.watch('ObjectID')
    const managementOnly = form.watch('ManagementOnly')
    const technology = form.watch('Technology')
    const datastore = form.watch('Datastore')

    // Fetch dropdown data
    const { data: layersData } = useQuery({
        queryKey: ['layers'],
        queryFn: async () => {
            const response = await classificationsApi.list()
            return response.data.data || []
        },
    })

    // What the selected classification says. The checkbox shows the EFFECTIVE value —
    // the object's override if it has one, otherwise this — so the box always reflects
    // what the canvas will actually do rather than what was typed here.
    const selectedClassificationId = form.watch('ClassificationID')
    const sharedByClassification = useMemo(() => {
        const c = ((layersData as any[]) || []).find((x) => Number(x.id) === Number(selectedClassificationId))
        return c ? Number(c.IsSharedService) === 1 : false
    }, [layersData, selectedClassificationId])

    const classGroups = useMemo(() => {
        const groups = new Map<string, any[]>()
        for (const c of (layersData as any[]) || []) {
            if (!groups.has(c.Category)) groups.set(c.Category, [])
            groups.get(c.Category)!.push(c)
        }
        return [...groups.entries()]
    }, [layersData])

    const { data: locationsData } = useQuery({
        queryKey: ['locations'],
        queryFn: async () => {
            const response = await locationsApi.list({ page: 1, limit: 1000 })
            return response.data.data || []
        },
    })

    const { data: teamsData } = useQuery({
        queryKey: ['teams'],
        queryFn: async () => {
            const response = await objectsApi.getTeams()
            return response.data?.data || response.data || []
        },
    })

    const { data: technologiesData, error: technologiesError, isLoading: technologiesLoading } = useQuery({
        queryKey: ['technologies'],
        queryFn: async () => {
            try {
                const response = await objectsApi.getTechnologies()
                console.log('Technologies API response:', response)
                const data = response.data?.data || response.data || []
                console.log('Technologies data:', data)
                return data
            } catch (error) {
                console.error('Error fetching technologies:', error)
                return []
            }
        },
    })

    const { data: backupSolutionsData, isLoading: backupSolutionsLoading, error: backupSolutionsError } = useQuery({
        queryKey: ['backupSolutions'],
        queryFn: async () => {
            const response = await objectsApi.getBackupSolutions()
            console.log('Backup solutions API response:', response)
            const data = response.data?.data || response.data || []
            console.log('Backup solutions data:', data)
            return data
        },
    })

    // Check ObjectID uniqueness
    useEffect(() => {
        if (!objectID || objectID.trim() === '' || isEditing) {
            setObjectIDError(null)
            return
        }

        const checkTimeout = setTimeout(async () => {
            setIsCheckingObjectID(true)
            try {
                const response = await objectsApi.checkObjectIDUnique(objectID)
                const isUnique = response.data?.data?.isUnique ?? response.data?.isUnique ?? false
                if (!isUnique) {
                    setObjectIDError(t('objects.objectIDNotUnique'))
                    form.setError('ObjectID', { message: t('objects.objectIDNotUnique') })
                } else {
                    setObjectIDError(null)
                    form.clearErrors('ObjectID')
                }
            } catch (error) {
                console.error('Error checking ObjectID uniqueness:', error)
            } finally {
                setIsCheckingObjectID(false)
            }
        }, 500) // Debounce

        return () => clearTimeout(checkTimeout)
    }, [objectID, isEditing, form, t])

    // Track previous technology to detect changes
    const prevTechnologyRef = useRef<number | null>(null)

    // Handle Technology category change - if Database and Datastore is not 1, show confirmation dialog
    useEffect(() => {
        // Only show dialog if technology actually changed (not on initial load)
        if (technology && technologiesData && prevTechnologyRef.current !== technology) {
            const selectedTech = technologiesData.find((t: any) => t.id === technology)
            if (selectedTech?.category === 'Database' && datastore !== 1) {
                // Show confirmation dialog to set datastore
                setDatastoreConfirmDialog({
                    open: true,
                    type: 'set',
                    pendingTechnology: technology,
                })
            } else if (selectedTech?.category !== 'Database' && datastore === 1) {
                // Show confirmation dialog to clear datastore
                setDatastoreConfirmDialog({
                    open: true,
                    type: 'clear',
                    pendingTechnology: technology,
                })
            }
            prevTechnologyRef.current = technology
        } else if (!technology) {
            prevTechnologyRef.current = null
        }
    }, [technology, technologiesData, datastore])

    // Handle datastore confirmation dialog actions
    const handleDatastoreConfirm = () => {
        if (datastoreConfirmDialog.type === 'set') {
            form.setValue('Datastore', 1)
        } else if (datastoreConfirmDialog.type === 'clear') {
            form.setValue('Datastore', 0)
        }
        setDatastoreConfirmDialog({ open: false, type: null, pendingTechnology: null })
    }

    const handleDatastoreCancel = () => {
        // Revert technology selection if user cancels
        if (datastoreConfirmDialog.pendingTechnology !== null) {
            form.setValue('Technology', prevTechnologyRef.current)
        }
        setDatastoreConfirmDialog({ open: false, type: null, pendingTechnology: null })
    }

    // Handle ManagementOnly change - if false, set BackupSolution to 0
    useEffect(() => {
        if (managementOnly === 0) {
            form.setValue('BackupSolution', 0)
        }
    }, [managementOnly, form])

    // RPO/RTO options (common values in minutes)
    const rpoRtoOptions = [
        { value: '0', label: '0 minutes' },
        { value: '15', label: '15 minutes' },
        { value: '30', label: '30 minutes' },
        { value: '60', label: '1 hour' },
        { value: '120', label: '2 hours' },
        { value: '240', label: '4 hours' },
        { value: '480', label: '8 hours' },
        { value: '1440', label: '24 hours' },
    ]

    const formatMinutes = (minutes: number | string | null): string => {
        if (!minutes) return ''
        const mins = typeof minutes === 'string' ? parseInt(minutes) : minutes
        return formatSecondsToDHM(mins * 60)
    }

    return (
        <div className="pb-2">
            {/* FOUR GROUPS SIDE BY SIDE, ONE FIELD PER ROW WITHIN EACH.

                Two stacked columns ran to 850px against a 500px panel on a 1440x820 laptop, so
                the dialog opened scrolled and half the form was out of sight: "Create or update
                object or service does not fit in the modal" (Andrew, 2026-09-22). The groups
                now stand side by side on a wide screen: Identity, Classification & Ownership,
                Recovery Objectives, Options, left to right from what the thing IS to what we
                DO with it. The tallest is about 360px, and nothing scrolls.

                Within a group nothing changes. Reading a form is a vertical act: you go down a
                column answering one question at a time. An earlier pass lost that by putting
                Classification, Responsible Team, Location and Technology shoulder to shoulder
                in rows of unrelated things. Each group is still one column, and the four
                options stay one above the other ("keep the four options vertical").

                Narrower screens fall back to two groups a row, then one. */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,0.9fr)]">
                {/* ── Identity ──────────────────────────────────────────────────────
                    What this thing is called, whether it is an Object or a Service,
                    what it is for, and the icon it is drawn with. The icon lives here
                    because it is part of the entity's identity — it used to sit beside
                    unrelated tick boxes. The description takes whatever height the
                    tallest group leaves, so the four groups end on one line. */}
                <FormSection title={t('objects.sections.identity', 'Identity')} className="flex flex-col">
                    <div className="flex flex-1 flex-col gap-5">
                            <FormField
                                control={form.control}
                                name="ObjectID"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('objects.objectID')}
                                            <RequiredTag />
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                placeholder={t('objects.objectIDPlaceholder')}
                                                disabled={isEditing}
                                                className="text-left"
                                            />
                                        </FormControl>
                                        {/* Guidance, not a failure — this used to be red body copy
                                            directly under the label and read as a validation error. */}
                                        {isEditing && (
                                            <FormDescription className="text-xs">
                                                {t('objects.objectIDCannotChange')}
                                            </FormDescription>
                                        )}
                                        {/* The reserved line keeps the form still while the availability
                                            check answers. Editing, the ID cannot change, so there is
                                            nothing to reserve room for. */}
                                        {isEditing ? (
                                            <FormMessage />
                                        ) : objectID && objectID.trim() !== '' ? (
                                            <div className="h-5 flex items-center gap-1.5 text-xs mt-1">
                                                {isCheckingObjectID ? (
                                                    <span className="text-muted-foreground">{t('objects.checkingObjectID', 'Checking...')}</span>
                                                ) : objectIDError ? (
                                                    <span className="text-destructive font-medium">{objectIDError}</span>
                                                ) : (
                                                    <span className="text-green-600 dark:text-green-400">{t('objects.objectIDAvailable', 'Available')}</span>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="h-5"><FormMessage /></div>
                                        )}
                                    </FormItem>
                                )}
                            />

                            <div className="flex items-end gap-4">
                            <FormField
                                control={form.control}
                                name="Icon"
                                render={({ field }) => (
                                    <FormItem className="shrink-0">
                                        <FormLabel>{t('objects.icon')}</FormLabel>
                                        <FormControl>
                                            <IconPicker
                                                value={field.value || null}
                                                onChange={field.onChange}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Object vs Service — tick to make this a Service instead of an Object
                                (unticked = Object). Beside the icon, under the Object ID it
                                re-characterises, in the same shell as the other tick-box options. */}
                            <FormField
                                control={form.control}
                                name="ObjectService"
                                render={({ field }) => (
                                    <FormItem className={`${OPTION_ROW} min-w-0 flex-1`}>
                                        <FormControl>
                                            <Checkbox
                                                checked={field.value === 'Service'}
                                                onCheckedChange={(checked) => field.onChange(checked ? 'Service' : 'Object')}
                                            />
                                        </FormControl>
                                        <FormLabel className={OPTION_LABEL}>
                                            <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
                                            {t('objects.serviceLabel', 'Service')}
                                            <InfoHint text={t('objects.serviceHint', 'A service is a collection of objects or services and does not have objects stored in the vaults.')} />
                                        </FormLabel>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="Description"
                            render={({ field }) => (
                                <FormItem className="flex flex-1 flex-col">
                                    <FormLabel>{t('objects.description')}</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            {...field}
                                            value={field.value || ''}
                                            placeholder={t('objects.descriptionPlaceholder')}
                                            rows={3}
                                            className="flex-1 resize-none text-left"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </FormSection>
                {/* ── Classification & ownership ────────────────────────────────────
                    Where this sits in the recovery stack and who answers for it. One
                    select a row, so the eye can run down the group. */}
                <FormSection title={t('objects.sections.classificationOwnership', 'Classification & Ownership')}>
                    <div className="space-y-5">
                        <FormField
                            control={form.control}
                            name="ClassificationID"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('objects.classification', 'Classification')}
                                        <RequiredTag />
                                    </FormLabel>
                                    <Select
                                        value={field.value?.toString() || ''}
                                        onValueChange={(value) => {
                                            field.onChange(value ? parseInt(value) : null)
                                        }}
                                    >
                                        <FormControl>
                                            <SelectTrigger className="text-left">
                                                <SelectValue placeholder={t('objects.selectClassification', 'Select classification')} />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {classGroups.map(([category, classes]: [string, any[]]) => (
                                                <div key={category}>
                                                    <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                        {category}
                                                    </div>
                                                    {classes.map((c: any) => (
                                                        <SelectItem key={c.id} value={String(c.id)}>
                                                            {c.Name}
                                                        </SelectItem>
                                                    ))}
                                                </div>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="ResponsibleTeam"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('objects.responsibleTeam')}</FormLabel>
                                    <Select
                                        value={field.value?.toString() || ''}
                                        onValueChange={(value) => {
                                            field.onChange(value ? parseInt(value) : null)
                                        }}
                                    >
                                        <FormControl>
                                            <SelectTrigger className="text-left">
                                                <SelectValue placeholder={t('objects.selectTeam')} />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <div className="px-2 py-1.5 border-b">
                                                <div className="relative">
                                                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                    <Input
                                                        placeholder={t('objects.searchTeams')}
                                                        value={teamSearch}
                                                        onChange={(e) => setTeamSearch(e.target.value)}
                                                        className="pl-8 h-8"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                            </div>
                                            {teamsData
                                                ?.filter((team: any) => {
                                                    if (!teamSearch) return true
                                                    const searchLower = teamSearch.toLowerCase()
                                                    const teamName = (team.teamName || '').toLowerCase()
                                                    return teamName.includes(searchLower)
                                                })
                                                .map((team: any) => (
                                                    <SelectItem key={team.id} value={team.id.toString()}>
                                                        {team.teamName}
                                                    </SelectItem>
                                                ))}
                                            {teamsData && teamsData.filter((team: any) => {
                                                if (!teamSearch) return true
                                                const searchLower = teamSearch.toLowerCase()
                                                const teamName = (team.teamName || '').toLowerCase()
                                                return teamName.includes(searchLower)
                                            }).length === 0 && teamSearch && (
                                                <SelectItem value="no-results" disabled>
                                                    {t('objects.noTeamsFound')}
                                                </SelectItem>
                                            )}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* A business service's own recovery targets, saved on their own (ServiceRecoveryTargets). */}
                        {isService && isEditing && !!objectID && <ServiceRecoveryTargets objectId={String(objectID)} />}

                        <FormField
                            control={form.control}
                            name="Location"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('objects.location')}
                                        {!isService && <RequiredTag />}
                                    </FormLabel>
                                    <Select
                                        value={field.value?.toString() || ''}
                                        onValueChange={(value) => {
                                            field.onChange(value ? parseInt(value) : null)
                                        }}
                                    >
                                        <FormControl>
                                            <SelectTrigger className="text-left">
                                                <SelectValue placeholder={t('objects.selectLocation')} />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {locationsData?.map((location: any) => (
                                                <SelectItem key={location.id} value={location.id.toString()}>
                                                    {location.description}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {!isService && (
                            <FormField
                                control={form.control}
                                name="Technology"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('objects.technology')}</FormLabel>
                                        <Select
                                            value={field.value?.toString() || ''}
                                            onValueChange={(value) => {
                                                prevTechnologyRef.current = field.value || null
                                                if (value === 'clear') {
                                                    field.onChange(null)
                                                } else {
                                                    field.onChange(value ? parseInt(value) : null)
                                                }
                                            }}
                                        >
                                            <FormControl>
                                                <SelectTrigger className="text-left">
                                                    <SelectValue placeholder={t('objects.selectTechnology')} />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <div className="px-2 py-1.5 border-b">
                                                    <div className="relative">
                                                        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                        <Input
                                                            placeholder={t('objects.searchTechnologies')}
                                                            value={technologySearch}
                                                            onChange={(e) => setTechnologySearch(e.target.value)}
                                                            className="pl-8 h-8"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                </div>
                                                {technologiesLoading ? (
                                                    <SelectItem value="loading" disabled>
                                                        Loading...
                                                    </SelectItem>
                                                ) : technologiesError ? (
                                                    <SelectItem value="error" disabled>
                                                        Error loading technologies
                                                    </SelectItem>
                                                ) : technologiesData && technologiesData.length > 0 ? (
                                                    <>
                                                        <SelectItem value="clear" className="text-muted-foreground">
                                                            {t('common.clear')}
                                                        </SelectItem>
                                                        {technologiesData
                                                            .filter((tech: any) => {
                                                                if (!technologySearch) return true
                                                                const searchLower = technologySearch.toLowerCase()
                                                                const name = (tech.name || '').toLowerCase()
                                                                const manufacturer = (tech.manufacturer || '').toLowerCase()
                                                                const category = (tech.category || '').toLowerCase()
                                                                const model = (tech.model || '').toLowerCase()
                                                                return name.includes(searchLower) ||
                                                                    manufacturer.includes(searchLower) ||
                                                                    category.includes(searchLower) ||
                                                                    model.includes(searchLower)
                                                            })
                                                            .map((tech: any) => (
                                                                <SelectItem key={tech.id} value={tech.id.toString()}>
                                                                    {tech.name}
                                                                </SelectItem>
                                                            ))}
                                                    </>
                                                ) : (
                                                    <SelectItem value="empty" disabled>
                                                        No technologies available
                                                    </SelectItem>
                                                )}
                                                {technologiesData && technologiesData.filter((tech: any) => {
                                                    if (!technologySearch) return true
                                                    const searchLower = technologySearch.toLowerCase()
                                                    const name = (tech.name || '').toLowerCase()
                                                    const manufacturer = (tech.manufacturer || '').toLowerCase()
                                                    const category = (tech.category || '').toLowerCase()
                                                    const model = (tech.model || '').toLowerCase()
                                                    return name.includes(searchLower) ||
                                                        manufacturer.includes(searchLower) ||
                                                        category.includes(searchLower) ||
                                                        model.includes(searchLower)
                                                }).length === 0 && technologiesData.length > 0 && technologySearch && (
                                                    <SelectItem value="no-results" disabled>
                                                        {t('objects.noTechnologiesFound')}
                                                    </SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                    </div>
                </FormSection>

                {/* ── Recovery objectives ───────────────────────────────────────────
                    The numbers the product exists to manage. Each custom-minutes box sits
                    beside the select it overrides, in the same row, so a field costs one
                    line rather than two. */}
                <FormSection title={t('objects.sections.recoveryObjectives', 'Recovery Objectives')}>
                    <div className="space-y-5">
                        {!isService && (
                            <FormField
                                control={form.control}
                                name="RPO"
                                render={({ field }) => {
                                    const currentValue = field.value?.toString() || ''
                                    const isDropdownValue = rpoRtoOptions.some(opt => opt.value === currentValue)
                                    const customMinutes = isDropdownValue ? '' : (typeof field.value === 'number' ? field.value : (currentValue ? parseInt(currentValue) : ''))

                                    return (
                                        <FormItem>
                                            <FormLabel>{t('objects.rpo')}</FormLabel>
                                            <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2">
                                                <Select
                                                    value={isDropdownValue ? currentValue : 'custom'}
                                                    onValueChange={(value) => {
                                                        if (value === 'custom') {
                                                            // Keep current custom value if it exists
                                                            if (customMinutes) {
                                                                field.onChange(customMinutes)
                                                            } else {
                                                                field.onChange(null)
                                                            }
                                                        } else {
                                                            field.onChange(value)
                                                        }
                                                    }}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="text-left">
                                                            <SelectValue placeholder={t('objects.selectRPO')} />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {rpoRtoOptions.map((option) => (
                                                            <SelectItem key={option.value} value={option.value}>
                                                                {option.label} ({formatMinutes(option.value)})
                                                            </SelectItem>
                                                        ))}
                                                        <SelectItem value="custom">{t('objects.customMinutes')}</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Input
                                                    type="number"
                                                    placeholder={t('objects.minutesPlaceholder', 'minutes')}
                                                    aria-label={`${t('objects.rpo')}: ${t('objects.enterMinutes')}`}
                                                    value={customMinutes}
                                                    onChange={(e) => {
                                                        const value = e.target.value
                                                        if (value) {
                                                            field.onChange(parseInt(value))
                                                        } else {
                                                            field.onChange(null)
                                                        }
                                                    }}
                                                    className="text-left"
                                                />
                                            </div>
                                            <FormMessage />
                                        </FormItem>
                                    )
                                }}
                            />
                        )}

                        <FormField
                            control={form.control}
                            name="RTO"
                            render={({ field }) => {
                                const currentValue = field.value?.toString() || ''
                                const isDropdownValue = rpoRtoOptions.some(opt => opt.value === currentValue)
                                const customMinutes = isDropdownValue ? '' : (typeof field.value === 'number' ? field.value : (currentValue ? parseInt(currentValue) : ''))

                                return (
                                    <FormItem>
                                        <FormLabel>{t('objects.rto')}</FormLabel>
                                        <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2">
                                            <Select
                                                value={isDropdownValue ? currentValue : 'custom'}
                                                onValueChange={(value) => {
                                                    if (value === 'custom') {
                                                        // Keep current custom value if it exists
                                                        if (customMinutes) {
                                                            field.onChange(customMinutes)
                                                        } else {
                                                            field.onChange(null)
                                                        }
                                                    } else {
                                                        field.onChange(value)
                                                    }
                                                }}
                                            >
                                                <FormControl>
                                                    <SelectTrigger className="text-left">
                                                        <SelectValue placeholder={t('objects.selectRTO')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {rpoRtoOptions.map((option) => (
                                                        <SelectItem key={option.value} value={option.value}>
                                                            {option.label} ({formatMinutes(option.value)})
                                                        </SelectItem>
                                                    ))}
                                                    <SelectItem value="custom">{t('objects.customMinutes')}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <Input
                                                type="number"
                                                placeholder={t('objects.minutesPlaceholder', 'minutes')}
                                                aria-label={`${t('objects.rto')}: ${t('objects.enterMinutes')}`}
                                                value={customMinutes}
                                                onChange={(e) => {
                                                    const value = e.target.value
                                                    if (value) {
                                                        field.onChange(parseInt(value))
                                                    } else {
                                                        field.onChange(null)
                                                    }
                                                }}
                                                className="text-left"
                                            />
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )
                            }}
                        />

                        <FormField
                            control={form.control}
                            name="Retention"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('objects.retention', 'Retention (days)')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            min={14}
                                            max={90}
                                            className="text-left"
                                            value={field.value ?? ''}
                                            onChange={(e) => {
                                                const v = e.target.value
                                                field.onChange(v === '' ? null : Math.min(90, Math.max(14, parseInt(v) || 0)))
                                            }}
                                        />
                                    </FormControl>
                                    <FormDescription className="text-xs">
                                        {t('objects.retentionHelp', 'Number of days to retain backups (14–90). Default is set in System Settings.')}
                                        <span className="block">{t('objects.retentionSubscription', 'Subscription is for 30 days.')}</span>
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </FormSection>
                {/* ── Options ───────────────────────────────────────────────────────
                    Every switch that changes how the object is handled, in one place
                    and in one shell. All four are Object-only, so a Service shows no
                    Options group at all rather than an empty heading. */}
                {/* Shown for services as well as objects now: a service can be a shared
                    service too, so the group can no longer be object-only. The original
                    three stay object-only inside it. */}
                {(
                    <FormSection title={t('objects.sections.options', 'Options')}>
                        <div className="space-y-5">
                            <div className="space-y-3">
                                {!isService && (<>
                                <FormField
                                    control={form.control}
                                    name="ManagementOnly"
                                    render={({ field }) => (
                                        <FormItem className={OPTION_ROW}>
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value === 1}
                                                    onCheckedChange={(checked) => {
                                                        field.onChange(checked ? 1 : 0)
                                                    }}
                                                />
                                            </FormControl>
                                            <FormLabel className={OPTION_LABEL}>
                                                <List className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                {t('objects.managementOnly')}
                                                <InfoHint text={t('objects.managementOnlyHint', 'The solution does not save any objects in the vaults. Orchestration and runbooks only.')} />
                                            </FormLabel>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="Datastore"
                                    render={({ field }) => (
                                        <FormItem className={OPTION_ROW}>
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value === 1}
                                                    onCheckedChange={(checked) => {
                                                        field.onChange(checked ? 1 : 0)
                                                    }}
                                                />
                                            </FormControl>
                                            <FormLabel className={OPTION_LABEL}>
                                                <Database className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                {t('objects.datastore')}
                                                <InfoHint text={t('objects.datastoreHint', 'Tagged as a data store for information on data stores in a rolled up object or service.')} />
                                            </FormLabel>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="Analytics"
                                    render={({ field }) => (
                                        <FormItem className={OPTION_ROW}>
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value === 'Yes'}
                                                    onCheckedChange={(checked) => {
                                                        field.onChange(checked ? 'Yes' : 'No')
                                                    }}
                                                />
                                            </FormControl>
                                            <FormLabel className={OPTION_LABEL}>
                                                <Radar className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                {t('objects.premiumAnalytics', 'Premium Analytics')}
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
                                                                className="inline-flex cursor-help text-muted-foreground hover:text-foreground"
                                                                aria-label={t('objects.premiumAnalyticsInfo', 'This is an add-on chargeable service.')}
                                                            >
                                                                <Info className="h-3.5 w-3.5" />
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="right" className="max-w-xs">
                                                            {t('objects.premiumAnalyticsInfo', 'This is an add-on chargeable service.')}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </FormLabel>
                                        </FormItem>
                                    )}
                                />
                                </>)}

                                {/* Shared service. The box shows the EFFECTIVE answer — this
                                    object's override if it has one, otherwise whatever its
                                    classification says — because that is what the canvas acts
                                    on. Ticking or unticking writes an explicit override; the
                                    reset link puts it back to following the classification,
                                    which a checkbox alone cannot express. */}
                                <FormField
                                    control={form.control}
                                    name="SharedServiceOverride"
                                    render={({ field }) => {
                                        const overridden = field.value !== null && field.value !== undefined
                                        const effective = overridden ? field.value === 1 : sharedByClassification
                                        return (
                                            <FormItem className="space-y-1">
                                                <div className={OPTION_ROW}>
                                                    <FormControl>
                                                        <Checkbox
                                                            checked={effective}
                                                            onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                                                        />
                                                    </FormControl>
                                                    <FormLabel className={OPTION_LABEL}>
                                                        <Share2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                        {t('objects.sharedService', 'Shared service')}
                                                        <InfoHint text={t('objects.sharedServiceHint', 'Foundational infrastructure that many things depend on — DNS, time, identity, the site itself. The dependency is still recorded; the visualisation can hide its edges so they stop crossing the whole diagram.')} />
                                                    </FormLabel>
                                                </div>
                                                <p className="pl-6 text-[11px] text-muted-foreground">
                                                    {overridden ? (
                                                        <>
                                                            {t('objects.sharedServiceOverridden', 'Set on this object, overriding its classification.')}{' '}
                                                            <button
                                                                type="button"
                                                                className="underline hover:text-foreground"
                                                                onClick={() => field.onChange(null)}
                                                            >
                                                                {t('objects.sharedServiceReset', 'Follow the classification instead')}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        t('objects.sharedServiceInherited', 'Following its classification, which says {{value}}.', {
                                                            value: sharedByClassification
                                                                ? t('common.yes', 'yes')
                                                                : t('common.no', 'no'),
                                                        })
                                                    )}
                                                </p>
                                            </FormItem>
                                        )
                                    }}
                                />
                            </div>

                            {/* Backup Solution only applies to a Management Only object, so it
                                appears directly beneath the switch that enables it. */}
                            {managementOnly === 1 && (
                                <div className="space-y-5">
                                    <FormField
                                        control={form.control}
                                        name="BackupSolution"
                                        render={({ field }) => {
                                            return (
                                                <FormItem>
                                                    <FormLabel>{t('objects.backupSolution')}</FormLabel>
                                                    <Select
                                                        value={field.value?.toString() || ''}
                                                        onValueChange={(value) => {
                                                            if (value === 'clear') {
                                                                field.onChange(null)
                                                            } else {
                                                                field.onChange(value ? parseInt(value) : null)
                                                            }
                                                        }}
                                                        disabled={backupSolutionsLoading}
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger className="text-left">
                                                                <SelectValue placeholder={backupSolutionsLoading ? t('common.loading') : t('objects.selectBackupSolution')} />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            <div className="px-2 py-1.5 border-b">
                                                                <div className="relative">
                                                                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                                    <Input
                                                                        placeholder={t('objects.searchBackupSolutions')}
                                                                        value={backupSolutionSearch}
                                                                        onChange={(e) => setBackupSolutionSearch(e.target.value)}
                                                                        className="pl-8 h-8"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onKeyDown={(e) => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {backupSolutionsError && (
                                                                <SelectItem value="error" disabled>
                                                                    {t('common.error')}: {backupSolutionsError.message}
                                                                </SelectItem>
                                                            )}
                                                            {backupSolutionsData && backupSolutionsData.length > 0 ? (
                                                                <>
                                                                    <SelectItem value="clear" className="text-muted-foreground">
                                                                        {t('common.clear')}
                                                                    </SelectItem>
                                                                    {backupSolutionsData
                                                                        .filter((solution: any) => {
                                                                            if (!backupSolutionSearch) return true
                                                                            const searchLower = backupSolutionSearch.toLowerCase()
                                                                            const name = (solution.name || '').toLowerCase()
                                                                            return name.includes(searchLower)
                                                                        })
                                                                        .map((solution: any) => (
                                                                            <SelectItem key={solution.id} value={solution.id.toString()}>
                                                                                {solution.name}
                                                                            </SelectItem>
                                                                        ))}
                                                                </>
                                                            ) : (
                                                                !backupSolutionsLoading && (
                                                                    <SelectItem value="no-data" disabled>
                                                                        {t('objects.noBackupSolutions')}
                                                                    </SelectItem>
                                                                )
                                                            )}
                                                            {backupSolutionsData && backupSolutionsData.filter((solution: any) => {
                                                                if (!backupSolutionSearch) return true
                                                                const searchLower = backupSolutionSearch.toLowerCase()
                                                                const name = (solution.name || '').toLowerCase()
                                                                return name.includes(searchLower)
                                                            }).length === 0 && backupSolutionsData.length > 0 && backupSolutionSearch && (
                                                                <SelectItem value="no-results" disabled>
                                                                    {t('objects.noBackupSolutionsFound')}
                                                                </SelectItem>
                                                            )}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </FormSection>
                )}
            </div>


            {/* Datastore Confirmation Dialog */}
            <Dialog open={datastoreConfirmDialog.open} onOpenChange={(open) => {
                if (!open) {
                    handleDatastoreCancel()
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {datastoreConfirmDialog.type === 'set'
                                ? t('objects.confirmSetDatastore')
                                : t('objects.confirmClearDatastore')}
                        </DialogTitle>
                        <DialogDescription>
                            {datastoreConfirmDialog.type === 'set' ? (
                                <>
                                    {t('objects.confirmSetDatastoreMessage')}
                                    <br /><br />
                                    <strong>{t('objects.datastoreAlignmentInfo')}</strong>
                                </>
                            ) : (
                                t('objects.confirmClearDatastoreMessage')
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleDatastoreCancel}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleDatastoreConfirm}
                        >
                            {datastoreConfirmDialog.type === 'set'
                                ? t('objects.setDatastore')
                                : t('objects.clearDatastore')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
